import type {
  NftToken,
  GeneralTokenCollectionType,
  ModifiedNftToken,
} from "@/features/bridge/types/open-sea-api.types";
import { produce } from "immer";
import { create } from "zustand";

interface NftStoreType {
  // State for the active token
  activeCollection: NftToken["collection"] | undefined;
  setActiveCollection: (val: string) => void;
  // State for the token collections from Alchemy NFT API (previously OpenSea)
  collections: GeneralTokenCollectionType;
  setCollections: (collections: GeneralTokenCollectionType) => void;
  tokensFound: boolean;
  setTokensFound: (val: boolean) => void;
  clearAndReset: () => void;
  getToken: (id: string) => ModifiedNftToken | undefined;
  toggleToken: (multiToken: ModifiedNftToken) => void;
  getSelectionCount: (collection: string | undefined) => number;
  getActiveCollection: () => GeneralTokenCollectionType[string] | undefined;
  getAllSelectionsCount: () => number;
  getSelectionStatus: (token: ModifiedNftToken) => boolean;
  selectAllTokens: () => void;
  deselectAllTokens: () => void;
  removeSuccessfullySentTokens: (
    tokens: ModifiedNftToken["identifier"][],
  ) => void;
}

export interface SelectedTokens {
  [collectionName: string]: NftToken[];
}

export const useNftTokenManager = create<NftStoreType>((set, get) => ({
  clearAndReset: () =>
    set({
      tokensFound: false,
      activeCollection: undefined,
      collections: {},
    }),

  tokensFound: false,
  setTokensFound: (val) => set({ tokensFound: val }),

  activeCollection: undefined,
  setActiveCollection: (val) => set({ activeCollection: val }),

  // Stores the collections from fetch response
  collections: {},
  setCollections: (collections) =>
    set({
      collections: collections,
      activeCollection: Object.keys(collections)[0],
    }),

  getActiveCollection: () => get().collections[get().activeCollection || ""],
  // Retrieve the selected token
  getToken: (id) =>
    Object.values(get().collections)
      .map((c) => c.tokenData)
      .flat()
      .find((token) => token.identifier === id),

  getSelectionCount: (collection) => {
    if (!collection) return 0;
    const collections = get().collections;
    const targetCollection = collections[collection];
    if (!targetCollection || !targetCollection.tokenData) return 0;

    return targetCollection.tokenData.reduce(
      (acc, token) =>
        token.amountToBridge ? acc + token.amountToBridge : acc,
      0,
    );
  },
  getAllSelectionsCount: () =>
    Object.values(get().collections)
      .map((collection) => collection.tokenData)
      .flat()
      .reduce(
        (acc, token) =>
          token.amountToBridge ? acc + token.amountToBridge : acc,
        0,
      ),
  getSelectionStatus: (multiToken) =>
    !!get()
      .collections[multiToken.collection]?.tokenData.flat()
      .find((token) => token.identifier === multiToken.identifier)
      ?.amountToBridge,

  toggleToken: (multiToken) =>
    set(
      produce((state: NftStoreType) => {
        // If collection count is one
        const collection = state.collections[multiToken.collection];
        if (!collection) return;
        const token = collection.tokenData.find(
          (token) => token.identifier === multiToken.identifier,
        );
        if (token) token.amountToBridge = token.amountToBridge ? 0 : 1;
      }),
    ),
  selectAllTokens: () =>
    set(
      produce((state: NftStoreType) => {
        const activeCollection = get().activeCollection;
        if (activeCollection) {
          state.collections[activeCollection].tokenData.map((token) => {
            token.amountToBridge = 1;
          });
        }
      }),
    ),
  deselectAllTokens: () =>
    set(
      produce((state: NftStoreType) => {
        const activeCollection = get().activeCollection;
        if (activeCollection) {
          state.collections[activeCollection].tokenData.forEach((token) => {
            token.amountToBridge = 0;
          });
        }
      }),
    ),
  removeSuccessfullySentTokens: (tokens) =>
    set(
      produce((state: NftStoreType) => {
        if (state.activeCollection) {
          console.log('🗑️ NFT Store: Removing tokens:', tokens);
          console.log('🗑️ NFT Store: Active collection:', state.activeCollection);
          console.log('🗑️ NFT Store: Before filter, token count:', state.collections[state.activeCollection].tokenData.length);

          state.collections[state.activeCollection].tokenData =
            state.collections[state.activeCollection].tokenData.filter(
              (t) => !tokens.includes(t.identifier),
            );

          console.log('🗑️ NFT Store: After filter, token count:', state.collections[state.activeCollection].tokenData.length);
        }
      }),
    ),
}));
